<template>
  <div class="results-page" :class="{ 'twitch-theme': isTwitchUser }">
    <!-- Jobs / Collections -->
    <card class="card results-collections-card mb-4">
      <div slot="header" class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="card-title mb-1">{{ tt("results.title", "Resultados") }}</h4>
          <p class="card-subtitle mb-0 text-muted">
            {{ tt("results.subtitle", "Jobs con clips renderizados") }} (<code>rendered_video_url</code>)
          </p>
        </div>
        <div class="text-right d-none d-md-block">
          <small class="text-muted d-block">
            {{ collectionsTotal }} {{ tt("results.jobs", "job") }}{{ collectionsTotal === 1 ? "" : "s" }}
            <span v-if="collectionsTotal > collectionsLimit">
              ({{ tt("results.pageShort", "pág.") }} {{ collectionsPage }} / {{ totalCollectionPages }})
            </span>
          </small>
          <small v-if="lastUpdated" class="text-muted">
            {{ tt("results.updated", "Actualizado") }}: {{ formattedLastUpdated }}
          </small>
        </div>
      </div>

      <div class="results-collections-body">
        <div v-if="loading" class="state-wrapper text-center">
          <clipr-loader
            :title="tt('common.loading', 'Cargando...')"
          />
        </div>

        <div v-else-if="error" class="state-wrapper text-center">
          <div class="error-state">
            <div class="error-state-icon">
              <i class="ti-alert-circle"></i>
            </div>
            <p class="error-state-title mb-2">{{ error }}</p>
            <button class="btn btn-outline-primary btn-sm" @click="loadCollectionsSummary">
              {{ tt("results.retry", "Reintentar") }}
            </button>
          </div>
        </div>

        <div v-else-if="jobCollections.length === 0" class="state-wrapper text-center">
          <div class="empty-state">
            <div class="empty-state-icon">
              <i class="ti-folder"></i>
            </div>
            <p class="empty-state-title">{{ tt("results.emptyTitle", "No hay clips renderizados para mostrar") }}</p>
            <p class="text-muted mb-0 small">
              {{ tt("results.emptyHint", "Esta pantalla muestra clips con") }} <code>rendered_video_url</code> {{ tt("results.emptyHintSuffix", "no nulo, agrupados por") }} <code>job_id</code>.
            </p>
          </div>
        </div>

        <div v-else class="collections-grid">
          <div
            v-for="job in jobCollections"
            :key="job.jobId"
            class="collection-item"
            @click="openPublicResults(job.jobId)"
          >
            <div class="collection-preview">
              <img
                v-if="effectiveCollectionThumbnailUrl(job)"
                :src="effectiveCollectionThumbnailUrl(job)"
                class="collection-preview-image"
                :alt="`Thumbnail ${job.jobIdShort || job.jobId}`"
                loading="lazy"
                @error="onCollectionThumbnailError(job.jobId)"
              />
              <div v-else class="collection-preview-icon">
                <i class="ti-video-camera"></i>
              </div>
            </div>
            <div class="collection-content">
              <div class="collection-info">
                <h5 class="collection-name" :title="job.streamName || job.sourceUsername || 'N/A'">
                  {{ job.streamName || job.sourceUsername || "N/A" }}
                </h5>
                <div class="collection-meta">
                  <span class="collection-meta-item" :title="job.sourceUsername || 'N/A'">
                    <i class="ti-user"></i>
                    {{ job.sourceUsername || "N/A" }}
                  </span>
                  <span class="collection-meta-item">
                    <i class="ti-video-camera"></i>
                    {{ job.totalClips }} {{ tt("results.clip", "clip") }}{{ job.totalClips === 1 ? "" : "s" }}
                  </span>
                </div>
                <p v-if="job.jobUpdatedAt" class="collection-updated text-muted">
                  {{ tt("results.updated", "Actualizado") }} {{ formatRelativeTime(job.jobUpdatedAt) }}
                </p>
              </div>
              <div class="collection-footer">
                <span class="job-status-badge collection-status-badge job-status-rendered">
                  {{ tt("results.rendered", "Renderizado") }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="jobCollections.length > 0 && collectionsTotal > collectionsLimit"
          class="d-flex justify-content-center align-items-center mt-4 flex-wrap collections-pagination"
        >
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mr-2"
            :disabled="collectionsPage <= 1 || loading"
            @click="goCollectionsPage(collectionsPage - 1)"
          >
            {{ tt("common.previous", "Anterior") }}
          </button>
          <span class="text-muted small mx-2">
            {{ tt("common.page", "Página") }} {{ collectionsPage }} {{ tt("common.of", "de") }} {{ totalCollectionPages }}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary ml-2"
            :disabled="collectionsPage >= totalCollectionPages || loading"
            @click="goCollectionsPage(collectionsPage + 1)"
          >
            {{ tt("common.next", "Siguiente") }}
          </button>
        </div>

        <div v-if="jobCollections.length > 0" class="state-wrapper text-center mt-4">
          <p class="text-muted mb-2">
            <i class="ti-info-alt"></i> {{ tt("results.openHint", "Haz clic en un job para abrir su página pública con las URLs renderizadas.") }}
          </p>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { Card } from "@/components";
import CliprLoader from "@/components/clipr/CliprLoader.vue";
import { supabase } from "@/supabase";
import { getPreferredSiteOrigin } from "@/utils/auth";
import { buildAuthHeaders } from "@/utils/impersonation";
import { useCurrentUserStore } from "@/stores/currentUser";

export default {
  name: "Results",
  components: {
    Card,
    CliprLoader,
  },
  data() {
    return {
      loading: false,
      error: null,
      lastUpdated: null,
      collectionRows: [],
      collectionsTotal: 0,
      collectionsPage: 1,
      collectionsLimit: 30,
      failedCollectionThumbnailJobIds: new Set(),
    };
  },
  computed: {
    isTwitchUser() {
      return useCurrentUserStore().isTwitchUser;
    },
    formattedLastUpdated() {
      if (!this.lastUpdated) return "";
      const d = this.lastUpdated;
      return (
        d.toLocaleDateString() +
        " " +
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    },
    totalCollectionPages() {
      return Math.max(1, Math.ceil(this.collectionsTotal / this.collectionsLimit) || 1);
    },
    jobCollections() {
      return (this.collectionRows || []).map((c) => {
        const id = c && c.id ? String(c.id) : "";
        let streamName = null;
        const meta = c && c.source_meta;
        if (meta && typeof meta === "object" && meta.title) {
          streamName = meta.title;
        }
        const ver = `${c.media_revision ?? ""}:${c.media_storage_state ?? ""}`;
        const rawThumb = c && c.job_thumbnail_url ? c.job_thumbnail_url : null;
        return {
          jobId: id,
          jobIdShort: id ? id.slice(0, 8) : "N/A",
          streamName,
          sourceUsername: (c && c.source_username) || null,
          totalClips: Number(c && c.rendered_results_count) || 0,
          thumbnailUrl: rawThumb ? this.withCacheBusterIfSafe(rawThumb, ver) : null,
          latestCreatedAt: c && c.created_at ? new Date(c.created_at).getTime() : 0,
          jobUpdatedAt: c && c.updated_at ? c.updated_at : c && c.created_at ? c.created_at : null,
        };
      });
    },
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    openPublicResults(jobId) {
      if (!jobId) return;
      const path = `/public/results/${jobId}`;
      // Twitch flow must always publish/open under Clipr public domain.
      const baseOrigin = this.isTwitchUser ? getPreferredSiteOrigin("clipr") : window.location.origin;
      const publicUrl = `${baseOrigin}${path}`;
      window.open(publicUrl, "_blank");
    },
    normalizeCollectionThumbnailUrl(rawValue) {
      const raw = String(rawValue || "").trim();
      if (!raw) return null;
      try {
        const parsed = new URL(raw, window.location.origin);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          return null;
        }
        return parsed.toString();
      } catch (_err) {
        return null;
      }
    },
    effectiveCollectionThumbnailUrl(jobCollection) {
      if (!jobCollection || !jobCollection.jobId) return null;
      if (this.failedCollectionThumbnailJobIds.has(jobCollection.jobId)) return null;
      return this.normalizeCollectionThumbnailUrl(jobCollection.thumbnailUrl);
    },
    onCollectionThumbnailError(jobId) {
      if (!jobId) return;
      this.failedCollectionThumbnailJobIds.add(jobId);
      this.$forceUpdate();
    },
    withCacheBusterIfSafe(url, version) {
      const raw = String(url || "").trim();
      if (!raw) return null;
      try {
        const parsed = new URL(raw, window.location.origin);
        if (
          parsed.pathname.startsWith("/public/jobs/")
          || parsed.pathname.startsWith("/public/media-v2/")
        ) {
          return raw;
        }
      } catch (_err) {
        // Mantener cache-buster para URLs legacy no parseables.
      }
      const lower = raw.toLowerCase();
      const isSigned =
        lower.includes("x-amz-signature=") ||
        lower.includes("x-amz-credential=") ||
        lower.includes("token=") ||
        lower.includes("sig=") ||
        lower.includes("signature=");
      if (isSigned) {
        return raw;
      }
      const token = version ? String(version) : String(Date.now());
      return raw.includes("?")
        ? `${raw}&v=${encodeURIComponent(token)}`
        : `${raw}?v=${encodeURIComponent(token)}`;
    },
    formatRelativeTime(value) {
      if (!value) return "";
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const now = new Date();
      const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
      const absSeconds = Math.abs(diffSeconds);
      let unit = "second";
      let amount = diffSeconds;

      if (absSeconds >= 60 && absSeconds < 3600) {
        unit = "minute";
        amount = Math.round(diffSeconds / 60);
      } else if (absSeconds >= 3600 && absSeconds < 86400) {
        unit = "hour";
        amount = Math.round(diffSeconds / 3600);
      } else if (absSeconds >= 86400 && absSeconds < 604800) {
        unit = "day";
        amount = Math.round(diffSeconds / 86400);
      } else if (absSeconds >= 604800 && absSeconds < 2592000) {
        unit = "week";
        amount = Math.round(diffSeconds / 604800);
      } else if (absSeconds >= 2592000) {
        unit = "month";
        amount = Math.round(diffSeconds / 2592000);
      }

      const displayAmount = Math.abs(amount);
      const suffix = amount < 0 ? this.tt("common.ago", "hace") : this.tt("common.in", "en");
      if (unit === "second") return `${displayAmount}s ${suffix}`;
      if (unit === "minute") return `${displayAmount}m ${suffix}`;
      if (unit === "hour") return `${displayAmount}h ${suffix}`;
      if (unit === "day") return `${displayAmount}d ${suffix}`;
      if (unit === "week") return `${displayAmount}w ${suffix}`;
      const pluralSuffix = this.$i18n?.locale === "es" ? "" : (displayAmount === 1 ? "" : "s");
      return `${displayAmount} ${unit}${pluralSuffix} ${suffix}`;
    },
    goCollectionsPage(nextPage) {
      const p = Number(nextPage);
      if (!p || p < 1 || p > this.totalCollectionPages) return;
      this.collectionsPage = p;
      this.loadCollectionsSummary();
    },
    async loadCollectionsSummary() {
      this.loading = true;
      this.error = null;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("common.noActiveSession", "No hay sesión activa."));
        }

        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
        const params = new URLSearchParams({
          page: String(this.collectionsPage),
          limit: String(this.collectionsLimit),
          rendered_results: "true",
        });
        const summaryResponse = await fetch(
          `${apiUrl}/momo/twitch/collections-summary?${params.toString()}`,
          {
            method: "GET",
            headers: buildAuthHeaders(session, { includeContentType: false }),
          }
        );
        if (!summaryResponse.ok) {
          const errData = await summaryResponse.json().catch(() => ({ detail: summaryResponse.statusText }));
          throw new Error(errData.detail || this.tt("results.loadError", "No se pudieron cargar las colecciones."));
        }
        const summaryPayload = await summaryResponse.json();
        this.collectionRows = Array.isArray(summaryPayload?.collections)
          ? summaryPayload.collections
          : [];
        this.collectionsTotal = Number(summaryPayload?.total) || 0;
        this.lastUpdated = new Date();
      } catch (err) {
        console.error("Error loading collections summary:", err);
        this.error = err.message || this.tt("results.genericError", "Error al cargar los resultados. Por favor, inténtalo de nuevo.");
        this.collectionRows = [];
        this.collectionsTotal = 0;
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    await this.loadCollectionsSummary();
  },
};
</script>

<style scoped>
.results-page {
  padding: 0 15px;
}

.state-wrapper {
  padding: 60px 20px;
}

.loading-icon {
  font-size: 32px;
}

/* Card - estilo VODClips */
.results-collections-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8ecf1;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.results-collections-body {
  padding: 24px;
  min-height: 200px;
}

.collections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

/* Collection item - diseño vertical como VODClips */
.collection-item {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  position: relative;
}

.collection-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: rgba(100, 116, 139, 0.9);
  border-radius: 4px 0 0 4px;
  z-index: 1;
}

.collection-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  border-color: #c5d0de;
}

.collection-preview {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  background: #020617;
}

.collection-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  background: #000;
}

.collection-preview-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 20px;
}

.collection-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 20px;
  gap: 16px;
}

.collection-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.collection-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.collection-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.collection-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.collection-meta-item i {
  font-size: 14px;
  opacity: 0.85;
  flex-shrink: 0;
}

.collection-updated {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.collection-footer {
  display: flex;
  align-items: center;
  padding-top: 4px;
  border-top: 1px solid #e8ecf1;
}

/* Job status badge */
.job-status-badge {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid rgba(34, 197, 94, 0.6);
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
}

.collection-item .collection-status-badge {
  position: static;
}

.job-status-rendered {
  border-color: rgba(34, 197, 94, 0.6);
  color: #16a34a;
}

/* Loading, error, empty states - estilo VODClips */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-icon {
  color: #64748b;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.error-state-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  font-size: 24px;
}

.error-state-title {
  font-weight: 600;
  color: #1e293b;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.empty-state-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: rgba(100, 116, 139, 0.08);
  border: 2px solid #e2e8f0;
  color: #64748b;
  font-size: 28px;
}

.empty-state-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

/* Estilos Twitch - tema oscuro con acentos púrpura */
.results-page.twitch-theme :deep(.card) {
  background: var(--clipr-bg-card) !important;
  border: 1px solid var(--clipr-border-color) !important;
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.8), 0 0 30px var(--clipr-purple-glow) !important;
  color: var(--clipr-text-primary) !important;
}

.results-page.twitch-theme :deep(.card-title),
.results-page.twitch-theme :deep(.card-subtitle) {
  color: var(--clipr-text-primary) !important;
}

.results-page.twitch-theme .text-muted {
  color: var(--clipr-text-secondary) !important;
}

.results-page.twitch-theme code {
  color: var(--clipr-purple-light) !important;
  background: rgba(168, 85, 247, 0.12) !important;
  padding: 2px 6px;
  border-radius: 4px;
}

.results-page.twitch-theme .collection-item {
  background: var(--clipr-bg-elevated) !important;
  border: 1px solid var(--clipr-border-color) !important;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.75) !important;
}

.results-page.twitch-theme .collection-item::before {
  background: rgba(148, 163, 184, 0.9) !important;
}

.results-page.twitch-theme .collection-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 22px 46px rgba(0, 0, 0, 0.9) !important;
  border-color: var(--clipr-border-purple) !important;
}

.results-page.twitch-theme .collection-item:hover::before {
  background: var(--clipr-purple-light) !important;
}

.results-page.twitch-theme .collection-preview {
  background: #020617 !important;
}

.results-page.twitch-theme .collection-preview-icon {
  color: #94a3b8 !important;
}

.results-page.twitch-theme .collection-content {
  border-color: transparent;
}

.results-page.twitch-theme .collection-name {
  color: var(--clipr-text-primary) !important;
}

.results-page.twitch-theme .collection-meta,
.results-page.twitch-theme .collection-meta-item {
  color: var(--clipr-text-secondary) !important;
}

.results-page.twitch-theme .collection-updated {
  color: var(--clipr-text-tertiary) !important;
}

.results-page.twitch-theme .collection-footer {
  border-top-color: var(--clipr-border-color) !important;
}

.results-page.twitch-theme .job-status-rendered {
  border-color: rgba(34, 197, 94, 0.6) !important;
  color: #4ade80 !important;
}

.results-page.twitch-theme .loading-icon {
  color: var(--clipr-purple-light) !important;
}

.results-page.twitch-theme .error-state-icon {
  background: rgba(248, 113, 113, 0.15) !important;
  color: #fecaca !important;
}

.results-page.twitch-theme .error-state-title {
  color: var(--clipr-text-primary) !important;
}

.results-page.twitch-theme .empty-state-icon {
  background: rgba(168, 85, 247, 0.08) !important;
  border-color: var(--clipr-border-purple) !important;
  color: var(--clipr-purple-light) !important;
}

.results-page.twitch-theme .empty-state-title {
  color: var(--clipr-text-primary) !important;
}

.results-page.twitch-theme :deep(.btn-outline-primary) {
  border-color: var(--clipr-purple-light) !important;
  color: var(--clipr-purple-light) !important;
}

.results-page.twitch-theme :deep(.btn-outline-primary:hover) {
  background: var(--clipr-purple-gradient) !important;
  border-color: transparent !important;
  color: white !important;
  box-shadow: 0 4px 12px var(--clipr-purple-glow) !important;
}

@media (max-width: 768px) {
  .collections-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .collection-content {
    padding: 16px;
  }

  .collection-name {
    font-size: 15px;
  }
}
</style>


